import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Top-of-page linear loader that signals route transitions while the next
 * page's lazy chunks are being fetched / rendered. The bar appears after a
 * small grace period (so cached / instant transitions don't flash) and is
 * dismissed once <NavigationReadyMarker /> mounts inside the page Suspense
 * boundary, which is React's signal that the new route is fully resolved.
 */

const isBrowser = typeof window !== 'undefined'

const listeners = new Set()
let readyPath = isBrowser ? window.location.pathname : null

function publishReadyPath(path) {
  if (path === readyPath) return
  readyPath = path
  listeners.forEach((listener) => listener())
}

function subscribe(listener) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getReadyPath() {
  return readyPath
}

function useReadyPath() {
  return useSyncExternalStore(subscribe, getReadyPath, getReadyPath)
}

/**
 * Mounted as a sibling of <Routes> inside the page Suspense boundary. It
 * renders nothing — but because it lives inside Suspense, it will only run
 * its layout effect once the new route's chunks have resolved, giving the
 * progress bar an authoritative "page ready" signal.
 */
export function NavigationReadyMarker() {
  const location = useLocation()
  useLayoutEffect(() => {
    publishReadyPath(location.pathname)
  }, [location.pathname])
  return null
}

/**
 * Thin progress strip pinned to the top of the viewport. Only rendered on
 * the client; harmless to render in trees that may also be SSR'd.
 */
export function NavigationProgressBar() {
  const location = useLocation()
  const ready = useReadyPath()
  const isNavigating = isBrowser && ready !== null && location.pathname !== ready

  const [visible, setVisible] = useState(false)
  const [progress, setProgress] = useState(0)
  const shownRef = useRef(false)

  useEffect(() => {
    if (!isNavigating) {
      if (!shownRef.current) return
      setProgress(100)
      const hide = window.setTimeout(() => {
        setVisible(false)
        setProgress(0)
        shownRef.current = false
      }, 220)
      return () => window.clearTimeout(hide)
    }

    let cancelled = false
    const timers = []
    const start = window.setTimeout(() => {
      if (cancelled) return
      shownRef.current = true
      setVisible(true)
      setProgress(15)
      timers.push(window.setTimeout(() => { if (!cancelled) setProgress(45) }, 220))
      timers.push(window.setTimeout(() => { if (!cancelled) setProgress(70) }, 600))
      timers.push(window.setTimeout(() => { if (!cancelled) setProgress(85) }, 1200))
      timers.push(window.setTimeout(() => { if (!cancelled) setProgress(92) }, 2200))
    }, 80)

    return () => {
      cancelled = true
      window.clearTimeout(start)
      timers.forEach((t) => window.clearTimeout(t))
    }
  }, [isNavigating])

  if (!visible) return null

  return (
    <div
      className="fixed left-0 right-0 top-0 z-[100065] h-[2px] pointer-events-none"
      style={{ marginTop: 'env(safe-area-inset-top, 0px)' }}
      aria-hidden
      role="progressbar"
    >
      <div
        className="h-full w-full bg-[var(--color-brand-04)] origin-left transition-transform duration-300 ease-out"
        style={{
          transform: `scaleX(${progress / 100})`,
          boxShadow: '0 0 8px var(--color-brand-04), 0 0 4px var(--color-brand-04)',
        }}
      />
    </div>
  )
}
